import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, Search, HelpCircle, Home, Users, FileText, Shield, Copy, X, Filter, ArrowUpRight, ArrowLeft, Bot } from 'lucide-react';
import { CreatePolicyModal } from './components/CreatePolicyModal';
import { CreditApplication } from './components/CreditApplication';
import { AdminDashboard } from './components/AdminDashboard';
import { AdvancedSearch } from './components/AdvancedSearch';
export function App() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [policyCreated, setPolicyCreated] = useState(false);
  const [currentPage, setCurrentPage] = useState<'policies' | 'application' | 'admin' | 'search'>('application');
  const [showStartArrow, setShowStartArrow] = useState(true);
  const handleOpenModal = () => {
    setIsModalOpen(true);
    setShowStartArrow(false);
  };
  const handleCloseModal = (created?: boolean) => {
    setIsModalOpen(false);
    if (created) {
      setPolicyCreated(true);
    }
  };
  return <div className="flex h-screen w-full bg-gray-50" data-id="element-0">
      {/* Sidebar */}
      <div className="w-16 bg-white border-r border-gray-200 flex flex-col items-center py-6 space-y-6 z-20" data-id="element-1">
        <button className="text-gray-600 hover:text-gray-900" data-id="element-2">
          <Home className="w-5 h-5" data-id="element-3" />
        </button>
        <button className="text-gray-600 hover:text-gray-900" data-id="element-4">
          <Users className="w-5 h-5" data-id="element-5" />
        </button>
        <button onClick={() => setCurrentPage('search')} className={`p-2 rounded-lg transition-colors ${currentPage === 'search' ? 'text-indigo-600 bg-indigo-50' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-6">
          <Search className="w-5 h-5" data-id="element-7" />
        </button>
        <div className="relative" data-id="element-8">
          {policyCreated && currentPage === 'policies' && <motion.div animate={{
          x: [0, 8, 0]
        }} transition={{
          duration: 1.2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute left-full ml-4 top-1/2 -translate-y-1/2 flex items-center gap-1.5 whitespace-nowrap" data-id="element-9">
              <ArrowLeft className="w-5 h-5 text-gray-900 -mr-1" data-id="element-10" />
              <span className="bg-gray-900 text-white text-sm font-medium px-3 py-1.5 rounded-lg shadow-lg" data-id="element-11">
                Go here
              </span>
            </motion.div>}
          <button onClick={() => setCurrentPage('application')} className={`p-2 rounded-lg transition-colors ${currentPage === 'application' ? 'text-indigo-600 bg-indigo-50' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-12">
            <FileText className="w-5 h-5" data-id="element-13" />
          </button>
        </div>
        <button onClick={() => setCurrentPage('policies')} className={`p-2 rounded-lg transition-colors ${currentPage === 'policies' ? 'text-indigo-600 bg-indigo-50' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-14">
          <Shield className={`w-5 h-5 ${currentPage === 'policies' ? 'fill-indigo-100' : ''}`} data-id="element-15" />
        </button>
        <button onClick={() => setCurrentPage('admin')} className={`p-2 rounded-lg transition-colors ${currentPage === 'admin' ? 'text-indigo-600 bg-indigo-50' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-16">
          <Bot className="w-5 h-5" data-id="element-17" />
        </button>
        <div className="flex-1" data-id="element-18" />
        <button className="text-gray-600 hover:text-gray-900" data-id="element-19">
          <Users className="w-5 h-5" data-id="element-20" />
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-21">
        {/* Top Navbar */}
        <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-4 z-10" data-id="element-22">
          <button className="text-gray-600 hover:text-gray-900" data-id="element-23">
            <Menu className="w-6 h-6" data-id="element-24" />
          </button>

          <img src="https://cdn.magicpatterns.com/uploads/qJVgA9PEfqwGLDv8KygeAy/Property_1Creit_Pulse_Logo_black_2_(16).png" alt="Credit Pulse" className="h-8 object-contain" data-id="element-25" />

          <div className="flex-1 max-w-2xl mx-8" data-id="element-26">
            <div className="relative" data-id="element-27">
              <input type="text" placeholder="Search Companies" className="w-full px-4 py-2 pl-4 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="element-28" />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-29" />
            </div>
          </div>

          <div className="flex items-center gap-3" data-id="element-30">
            <button className="w-10 h-10 rounded-full border-2 border-indigo-400 flex items-center justify-center text-indigo-600 hover:bg-indigo-50" data-id="element-31">
              <HelpCircle className="w-5 h-5" data-id="element-32" />
            </button>
            <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium" data-id="element-33">
              IN
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto" data-id="element-34">
          {currentPage === 'admin' ? <AdminDashboard data-id="element-35" /> : currentPage === 'search' ? <AdvancedSearch onResultClick={() => setCurrentPage('application')} data-id="element-36" /> : currentPage === 'policies' ? <div className="px-8 py-6 max-w-7xl mx-auto" data-id="element-37">
              <div className="flex items-center justify-between mb-6" data-id="element-38">
                <div data-id="element-39">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-40">
                    Credit Policy Management
                  </h1>
                  <p className="text-gray-600" data-id="element-41">
                    Manage policies for New and Existing customers
                  </p>
                </div>
                <div className="relative" data-id="element-42">
                  {showStartArrow && !isModalOpen && <motion.div animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 1.2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="absolute -bottom-14 right-0 flex items-center gap-1.5 whitespace-nowrap" data-id="element-43">
                      <span className="bg-gray-900 text-white text-sm font-medium px-3 py-1.5 rounded-lg shadow-lg" data-id="element-44">
                        Click here to start
                      </span>
                      <ArrowUpRight className="w-5 h-5 text-gray-900 -ml-1 -mt-4" data-id="element-45" />
                    </motion.div>}
                  <button onClick={handleOpenModal} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 font-medium relative z-10" data-id="element-46">
                    <span className="text-lg" data-id="element-47">+</span>
                    New Policy
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6" data-id="element-48">
                <div className="flex gap-8" data-id="element-49">
                  <button className="pb-3 px-1 border-b-2 border-indigo-600 text-indigo-600 font-medium" data-id="element-50">
                    New Customer Policies
                  </button>
                  <button className="pb-3 px-1 text-gray-600 hover:text-gray-900 font-medium" data-id="element-51">
                    Existing Customer Policies
                  </button>
                </div>
              </div>

              {/* Search and Filter */}
              <div className="flex gap-4 mb-8" data-id="element-52">
                <div className="relative flex-1 max-w-sm" data-id="element-53">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-54" />
                  <input type="text" placeholder="Search policies" className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="element-55" />
                </div>
                <button className="px-4 py-2 border border-gray-300 rounded-lg flex items-center gap-2 text-gray-700 hover:bg-gray-50 font-medium" data-id="element-56">
                  <Filter className="w-4 h-4" data-id="element-57" />
                  Show Default Only
                </button>
              </div>

              {/* Policy Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-58">
                {/* Default Policy Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-59">
                  <div className="flex items-start justify-between mb-4" data-id="element-60">
                    <div data-id="element-61">
                      <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-62">
                        Credit Pulse Default Policy
                      </h3>
                      <div className="flex items-center gap-2 mb-3" data-id="element-63">
                        <span className="px-2 py-1 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded" data-id="element-64">
                          Default
                        </span>
                        <span className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded" data-id="element-65">
                          v1
                        </span>
                      </div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600" data-id="element-66">
                      <Copy className="w-5 h-5" data-id="element-67" />
                    </button>
                  </div>

                  <div className="space-y-1 mb-4 text-sm text-gray-600" data-id="element-68">
                    <p data-id="element-69">Type: New Customer</p>
                    <p data-id="element-70">
                      Last updated on Mar 27, 2026 by Integration Testing User
                    </p>
                  </div>

                  <button className="w-full bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 py-2.5 rounded-lg font-medium" data-id="element-71">
                    Manage Policy &gt;
                  </button>
                </div>

                {/* New Policy Card */}
                <AnimatePresence data-id="element-72">
                  {policyCreated && <motion.div initial={{
                opacity: 0,
                scale: 0.9,
                y: 20
              }} animate={{
                opacity: 1,
                scale: 1,
                y: 0
              }} className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-73">
                      <div className="flex items-start justify-between mb-4" data-id="element-74">
                        <div data-id="element-75">
                          <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-76">
                            My Company's Credit Policy
                          </h3>
                          <div className="flex items-center gap-2 mb-3" data-id="element-77">
                            <span className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded" data-id="element-78">
                              v1
                            </span>
                          </div>
                        </div>
                        <button className="text-gray-400 hover:text-gray-600" data-id="element-79">
                          <Copy className="w-5 h-5" data-id="element-80" />
                        </button>
                      </div>

                      <div className="space-y-1 mb-4 text-sm text-gray-600" data-id="element-81">
                        <p data-id="element-82">Type: New Customer</p>
                        <p data-id="element-83">Last updated on Mar 29, 2026 by You</p>
                      </div>

                      <button className="w-full bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 py-2.5 rounded-lg font-medium" data-id="element-84">
                        Manage Policy &gt;
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div> : <CreditApplication data-id="element-85" />}
        </main>
      </div>

      {/* Modal */}
      {isModalOpen && <CreatePolicyModal onClose={handleCloseModal} data-id="element-86" />}
    </div>;
}